import { useCallback, useEffect, useMemo, useRef, useState, ChangeEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from './ui/ConfirmDialog';
import { Spinner } from './ui/Spinner';
import {
  Upload, RotateCcw, FileJson, User as UserIcon, BookOpen,
  CheckCircle2, Clock, FileWarning, Send, EyeOff, Trash2,
  ListChecks, ChevronUp,
} from 'lucide-react';
import type { Profile, Course } from '../types/database';
import type {
  BimEntrega, BimEntregaStatus, BimItemEstado,
  CommentChangedPayload, EstadoChangedPayload,
  ComentariosOverride, EstadosOverride,
} from '../types/bim';

/** Estructura que el iframe envía vía `matrizReady`. */
interface MatrizItem {
  id:        string;
  item:      string;
  categoria: string;
  criterio:  string;
  estado:    BimItemEstado;
  comentarioAuto: string;
}
import {
  listMyEntregas, listEntregasOfAlumno,
  listCursos, listAlumnosOfCurso,
  loadComentariosOfEntrega, downloadEntregaJSON,
  createEntregaWithUpload, replaceEntregaJSON,
  upsertComentario, updateEntregaStatus, updateEntregaNota,
  updateEntregaComentarioGeneral,
  syncEntregaToEvaluation,
  deleteEntrega as deleteEntregaSb,
} from '../lib/bim';

/**
 * Pestaña "Dashboard BIM" — embebe el dashboard interactivo
 * (`public/bim-dashboard/index.html`) y le pasa por postMessage el data.json
 * más los comentarios y estados ya guardados en Supabase.
 *
 * Flujo:
 *  - ALUMNO: ve sus propias entregas (RLS las filtra por alumno_id = auth.uid()).
 *  - DOCENTE/ADMIN: elige alumno + entrega; si no existe, sube data.json
 *    desde el plugin de Revit y se crea la fila + se guarda en Storage.
 *
 *  Cualquier `commentChanged` / `estadoChanged` que el iframe emita se
 *  persiste vía upsert en `bim_comentarios`.
 *
 *  Demo fallback: si no hay entrega real cargada, muestra el data.json
 *  estático que ya viene en `/bim-dashboard/data.json` (el de demo).
 */
export function CorreccionesBim() {
  const { profile } = useAuth();
  const confirm = useConfirm();
  const isStaff = profile?.role === 'docente' || profile?.role === 'admin';
  const mode    = isStaff ? 'docente' : 'alumno';

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeReady, setIframeReady] = useState(false);

  // Lista de entregas (alumno: las propias / docente: las del alumno seleccionado)
  const [entregas, setEntregas] = useState<BimEntrega[]>([]);
  const [entregaSel, setEntregaSel] = useState<BimEntrega | null>(null);

  // Solo docente
  const [cursos, setCursos] = useState<Course[]>([]);
  const [cursoSel, setCursoSel] = useState<string>('');
  const [alumnos, setAlumnos] = useState<Profile[]>([]);
  const [alumnoSel, setAlumnoSel] = useState<string>('');
  const [entregaNumDraft, setEntregaNumDraft] = useState<number>(1);

  // Datos del modelo cargado en el iframe
  const [data, setData] = useState<unknown>(null);
  const [fileName, setFileName] = useState<string>('—');

  // Overrides locales — fuente de verdad mientras el componente está montado.
  // Se cargan al cambiar de entrega y se actualizan tanto desde el iframe
  // (commentChanged/estadoChanged) como desde el sidebar de la matriz.
  const [comentariosOverride, setComentariosOverride] = useState<ComentariosOverride>({});
  const [estadosOverride,     setEstadosOverride]     = useState<EstadosOverride>({});

  // Matriz de revisión emitida por el iframe via `matrizReady`
  const [matrizItems, setMatrizItems] = useState<MatrizItem[]>([]);
  const [matrizCollapsed, setMatrizCollapsed] = useState(false);

  // Comentario general / conclusiones del docente sobre la entrega completa
  // (se guarda en bim_entregas.comentario_general).
  const [comentarioGeneral, setComentarioGeneral] = useState<string>('');

  // Edit state docente (footer combinado)
  const [notaDraft, setNotaDraft] = useState<string>('');
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // UI
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  // ─── Persistencia de la selección del docente ──────────────────────
  // Cuando React re-ejecuta el useEffect que carga cursos (por cambio de
  // `profile`, refresh de auth, blur/focus de tab, etc.) NO queremos que
  // la selección de curso/alumno/entrega vuelva al primer elemento.
  // Persistimos en localStorage por user.id y respetamos el valor guardado
  // si sigue existiendo en la lista actual.
  type PersistedSel = { cursoId?: string; alumnoId?: string; entregaNum?: number };
  const lsKey = profile ? `bim_seleccion_${profile.id}` : null;
  const readPersisted = useCallback((): PersistedSel => {
    if (!lsKey) return {};
    try {
      const raw = localStorage.getItem(lsKey);
      return raw ? (JSON.parse(raw) as PersistedSel) : {};
    } catch {
      return {};
    }
  }, [lsKey]);
  const writePersisted = useCallback((patch: PersistedSel) => {
    if (!lsKey) return;
    try {
      const merged = { ...readPersisted(), ...patch };
      localStorage.setItem(lsKey, JSON.stringify(merged));
    } catch { /* localStorage lleno o privado — no es crítico */ }
  }, [lsKey, readPersisted]);

  // ─── 1. Cargar listas iniciales ──────────────────────────────────
  // ATENCIÓN — dependencias:
  //   Usamos `profile?.id` (string) y NO `profile` (objeto).
  //
  //   Cuando el usuario alterna entre pestañas/ventanas/Revit y vuelve,
  //   Supabase Auth dispara `TOKEN_REFRESHED` → AuthContext refetchea el
  //   profile → `setProfile(nuevoObjeto)`. Es la MISMA data pero distinta
  //   referencia, así que `[profile, ...]` activa el efecto y termina
  //   reseteando el curso/alumno seleccionado (bug reportado).
  //
  //   Con `[profile?.id, ...]` React compara strings: si el id es el
  //   mismo, el efecto NO se vuelve a disparar al volver al tab.
  //   Solo corre cuando cambia el usuario logueado, que es lo correcto.
  useEffect(() => {
    if (!profile) return;
    let cancel = false;

    (async () => {
      try {
        if (isStaff) {
          const cs = await listCursos();
          if (cancel) return;
          setCursos(cs);
          if (cs.length > 0) {
            // Respetar la selección actual o la persistida.
            // Solo caer al cs[0] si ninguna de las dos sigue siendo válida.
            // (Antes hacíamos `setCursoSel(cs[0].id)` siempre, lo cual
            // reseteaba la selección del docente cada vez que React
            // reejecutaba este efecto — bug reportado.)
            const persisted = readPersisted();
            setCursoSel(prev => {
              if (prev && cs.some(c => c.id === prev)) return prev;
              if (persisted.cursoId && cs.some(c => c.id === persisted.cursoId)) {
                return persisted.cursoId;
              }
              return cs[0].id;
            });
            // También restauramos el draft de número de entrega (si lo había)
            // así cuando carguen las entregas del alumno se preselecciona la
            // misma que el docente estaba revisando.
            if (persisted.entregaNum && persisted.entregaNum >= 1 && persisted.entregaNum <= 4) {
              setEntregaNumDraft(persisted.entregaNum);
            }
          }
        } else {
          const mine = await listMyEntregas();
          if (cancel) return;
          setEntregas(mine);
          if (mine.length > 0) {
            setEntregaSel(mine[0]);
          } else {
            // Alumno sin entregas publicadas: NO cargamos el demo (mostraría
            // datos falsos). Apagamos el loader y dejamos que el render
            // muestre el empty state ("Aún no hay entregas publicadas...").
            setEntregaSel(null);
            setData(null);
            setLoading(false);
          }
        }
      } catch (e: any) {
        if (!cancel) setError(e?.message ?? 'Error cargando datos iniciales');
      }
    })();

    return () => { cancel = true; };
  }, [profile?.id, isStaff]);

  // ─── 2a. Docente eligió curso → traer sus alumnos ────────────────
  useEffect(() => {
    if (!isStaff) return;
    if (!cursoSel) {
      setAlumnos([]);
      setAlumnoSel('');
      return;
    }
    let cancel = false;
    (async () => {
      const als = await listAlumnosOfCurso(cursoSel);
      if (cancel) return;
      setAlumnos(als);
      // Respetar el alumno actual (si sigue en la lista) o el persistido.
      // Esto evita que un re-fetch del useEffect 1 (con su efecto cascada
      // sobre cursoSel) nos rebote del alumno que el docente estaba viendo
      // al alumno [0] de la lista.
      const persisted = readPersisted();
      setAlumnoSel(prev => {
        if (prev && als.some(a => a.id === prev)) return prev;
        if (persisted.alumnoId && als.some(a => a.id === persisted.alumnoId)) {
          return persisted.alumnoId;
        }
        return als[0]?.id ?? '';
      });
      if (als.length === 0) {
        setEntregaSel(null);
        loadDemoFallback();
      }
    })();
    return () => { cancel = true; };
  }, [isStaff, cursoSel, readPersisted]);

  // ─── 2b. Docente eligió alumno → traer sus entregas ──────────────
  useEffect(() => {
    if (!isStaff || !alumnoSel) return;
    let cancel = false;

    (async () => {
      const list = await listEntregasOfAlumno(alumnoSel);
      if (cancel) return;
      setEntregas(list);
      // Si la entrega del draft existe, seleccionarla; si no, dejar entregaSel en null
      const match = list.find(e => e.entrega_num === entregaNumDraft);
      setEntregaSel(match ?? null);
      if (!match) loadDemoFallback(); // mostrar el demo mientras no haya entrega real
    })();

    return () => { cancel = true; };
  }, [isStaff, alumnoSel]);

  // ─── 3. Cambio de entrega seleccionada → recargar data + overrides ──
  // OJO: NO reseteamos `matrizItems` acá. Si lo reseteamos, una transición
  // momentánea de `entregaSel` a null (por ej. durante un re-fetch de
  // entregas) deja la sidebar atascada en "Cargando matriz…" hasta que el
  // iframe vuelva a emitir `matrizReady`, lo que puede no pasar si el
  // dashboard ya estaba renderizado. Como el iframe SIEMPRE re-emite
  // matrizReady cuando cambia la entrega activa, dejamos que él pise los
  // items. Si entregaSel pasa a null, mantenemos los items previos pero
  // marcamos `disabled` en el sidebar (ver render).
  useEffect(() => {
    if (!entregaSel) {
      // No tocamos los overrides ni la matriz — los limpiará el siguiente
      // entregaSel real cuando llegue.
      return;
    }
    let cancel = false;

    (async () => {
      setLoading(true);
      setError(null);

      // Disparamos las 2 cargas en paralelo. Lo importante es que los
      // OVERRIDES estén listos ANTES de setear `data`, porque setData
      // dispara useEffect 4 → sendInit y queremos que ese init lleve los
      // overrides ya cargados (no vacíos). Si esperamos los overrides
      // primero y los seteamos antes que data, el init va a leerlos.
      const [json, ovr] = await Promise.all([
        entregaSel.data_path ? downloadEntregaJSON(entregaSel.data_path) : Promise.resolve(null),
        loadComentariosOfEntrega(entregaSel.id),
      ]);
      if (cancel) return;

      // 1) Setear overrides PRIMERO (actualiza el ref) — el init siguiente
      // los va a leer del ref correctamente.
      comentariosOverrideRef.current = ovr.comentariosOverride;
      estadosOverrideRef.current     = ovr.estadosOverride;
      setComentariosOverride(ovr.comentariosOverride);
      setEstadosOverride(ovr.estadosOverride);
      setNotaDraft(entregaSel.nota_final?.toString() ?? '');
      setComentarioGeneral(entregaSel.comentario_general ?? '');

      // 2) Setear data (esto dispara el useEffect 4 → sendInit con overrides
      // ya disponibles en los refs).
      if (json) {
        setData(json);
        setFileName(entregaSel.archivo_nombre || `entrega-${entregaSel.entrega_num}.json`);
      } else {
        setError('No se pudo descargar el modelo. Cargá el archivo de nuevo.');
        loadDemoFallback();
      }
    })();

    return () => { cancel = true; };
  }, [entregaSel?.id]);

  // ─── 4. Cuando hay data + iframe listo, mandar init ──────────────
  useEffect(() => {
    if (!data || !iframeReady) return;
    sendInit();
  }, [data, iframeReady, entregaSel?.id]);

  const sendInit = () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow || !data) return;

    // El nombre que aparece en el "Hola, X" del banner. Para docente/admin
    // mostramos el nombre del docente logueado; el alumno del modelo se sigue
    // viendo en el header del componente y en el subtítulo.
    const viewerName = profile?.full_name || profile?.email || '';
    let viewerSubject: string | undefined;
    if (isStaff && entregaSel) {
      const al = alumnos.find(a => a.id === entregaSel.alumno_id);
      const an = al?.full_name || al?.email || '—';
      viewerSubject = `Modo revisor — alumno: ${an} · Entrega ${entregaSel.entrega_num}`;
    }

    // IMPORTANTE: leemos los overrides desde refs (no desde el closure), así
    // siempre enviamos el state MÁS RECIENTE — esto evita que un re-init
    // tardío (causado por StrictMode o por un cambio de `data`) pise una
    // edición fresca del docente con un payload viejo.
    iframe.contentWindow.postMessage({
      type: 'init',
      payload: {
        mode,
        entrega: entregaSel ? {
          id:                 entregaSel.id,
          entrega_num:        entregaSel.entrega_num,
          status:             entregaSel.status,
          nota_final:         entregaSel.nota_final,
          comentario_general: entregaSel.comentario_general ?? '',
        } : null,
        data,
        comentariosOverride: comentariosOverrideRef.current,
        estadosOverride:     estadosOverrideRef.current,
        viewerName,
        viewerSubject,
      },
    }, '*');
  };

  // ─── 5. Listener de mensajes del iframe (registra UNA sola vez) ────
  // Usamos un ref para acceder al `entregaSel` actual desde el listener,
  // así no hace falta re-registrarlo en cada cambio. Re-registrar abre una
  // ventana microsegundo donde un `matrizReady` puede llegar sin handler
  // — eso causaba que la matriz se "cargue y desaparezca".
  const entregaSelRef = useRef<BimEntrega | null>(null);
  useEffect(() => { entregaSelRef.current = entregaSel; }, [entregaSel]);

  const persistComentario = useCallback(async (p: CommentChangedPayload) => {
    const cur = entregaSelRef.current;
    if (!cur) return;
    const item_id = p.section_key || p.itemId;
    if (!item_id) return;
    await upsertComentario({
      entrega_id:   cur.id,
      item_id,
      comment_text: p.comment_text ?? null,
    });
  }, []);

  const persistEstado = useCallback(async (p: EstadoChangedPayload) => {
    const cur = entregaSelRef.current;
    if (!cur) return;
    const item_id = p.section_key || p.itemId;
    if (!item_id || !p.estado) return;
    await upsertComentario({
      entrega_id: cur.id,
      item_id,
      estado:     p.estado,
    });
  }, []);

  useEffect(() => {
    const onMsg = async (ev: MessageEvent) => {
      const iframe = iframeRef.current;
      if (!iframe || ev.source !== iframe.contentWindow) return;
      const msg = (ev.data || {}) as { type?: string; payload?: any };

      switch (msg.type) {
        case 'ready':
          setIframeReady(true);
          break;
        case 'rendered':
          setLoading(false);
          break;
        case 'matrizReady':
          if (Array.isArray(msg.payload?.items) && msg.payload.items.length > 0) {
            setMatrizItems(msg.payload.items as MatrizItem[]);
          }
          break;
        case 'commentChanged': {
          const p = msg.payload as CommentChangedPayload;
          const itemId = p?.section_key || p?.itemId;
          if (itemId) {
            setComentariosOverride(prev => {
              const next = { ...prev };
              if (p.comment_text == null || p.comment_text === '') delete next[itemId];
              else next[itemId] = p.comment_text;
              return next;
            });
          }
          await persistComentario(p);
          break;
        }
        case 'commentReset': {
          const p = msg.payload as CommentChangedPayload;
          const itemId = p?.section_key || p?.itemId;
          if (itemId) {
            setComentariosOverride(prev => {
              const next = { ...prev };
              delete next[itemId];
              return next;
            });
          }
          await persistComentario({ ...(p ?? {}), comment_text: null });
          break;
        }
        case 'estadoChanged': {
          const p = msg.payload as EstadoChangedPayload;
          const itemId = p?.section_key || p?.itemId;
          if (itemId && p.estado) {
            setEstadosOverride(prev => ({ ...prev, [itemId]: p.estado! }));
          }
          await persistEstado(p);
          break;
        }
        case 'comentarioGeneralChanged': {
          // Edición desde la fila "Comentarios Generales" de la matriz interna.
          const texto = String(msg.payload?.comentario_general ?? '');
          const cur = entregaSelRef.current;
          if (!cur) break;
          setComentarioGeneral(texto);
          setEntregaSel(prev => prev ? { ...prev, comentario_general: texto } : prev);
          // Debounce el save para no spammear
          if (generalSaveTimer.current) clearTimeout(generalSaveTimer.current);
          generalSaveTimer.current = setTimeout(() => {
            updateEntregaComentarioGeneral(cur.id, texto);
          }, 600);
          break;
        }
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [persistComentario, persistEstado]);

  // ─── 5b. Edición desde el sidebar React → upsert + push al iframe ────
  // Refs para evitar stale closures cuando el usuario tipea rápido o cuando
  // hay debouncing — los handlers leen el valor más reciente desde el ref.
  const comentariosOverrideRef = useRef<ComentariosOverride>({});
  const estadosOverrideRef     = useRef<EstadosOverride>({});
  useEffect(() => { comentariosOverrideRef.current = comentariosOverride; }, [comentariosOverride]);
  useEffect(() => { estadosOverrideRef.current     = estadosOverride;     }, [estadosOverride]);

  // Debounce timers por item para no spammear Supabase mientras el docente
  // escribe (cada cambio empuja al iframe instantáneamente, pero al server
  // se envía 500ms después de que dejó de tipear).
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const pushComentarioToIframe = (next: ComentariosOverride) => {
    iframeRef.current?.contentWindow?.postMessage({
      type: 'updateComentariosOverride',
      payload: next,
    }, '*');
  };

  const pushEstadoToIframe = (next: EstadosOverride) => {
    iframeRef.current?.contentWindow?.postMessage({
      type: 'updateEstadosOverride',
      payload: next,
    }, '*');
  };

  const editComentarioFromSidebar = (itemId: string, value: string) => {
    const cur = entregaSelRef.current;
    if (!cur) {
      console.warn('[CUBUS-react] editComentarioFromSidebar sin entregaSel — ignorando');
      return;
    }
    const next: ComentariosOverride = { ...comentariosOverrideRef.current };
    if (!value) delete next[itemId];
    else        next[itemId] = value;
    comentariosOverrideRef.current = next;
    setComentariosOverride(next);
    console.log('[CUBUS-react] sidebar editó item', itemId, '→ enviando postMessage al iframe. value len:', value.length);
    pushComentarioToIframe(next);

    if (saveTimers.current[itemId]) clearTimeout(saveTimers.current[itemId]);
    saveTimers.current[itemId] = setTimeout(async () => {
      console.log('[CUBUS-react] debounce → upsert a Supabase para', itemId);
      const ok = await upsertComentario({
        entrega_id:   cur.id,
        item_id:      itemId,
        comment_text: value || null,
      });
      console.log('[CUBUS-react] upsert resultado:', ok ? 'OK ✓' : 'FALLÓ ✗');
    }, 500);
  };

  const editEstadoFromSidebar = (itemId: string, estado: BimItemEstado) => {
    const cur = entregaSelRef.current;
    if (!cur) return;
    const next: EstadosOverride = { ...estadosOverrideRef.current, [itemId]: estado };
    estadosOverrideRef.current = next;
    setEstadosOverride(next);
    pushEstadoToIframe(next);
    setMatrizItems(prev => prev.map(it => it.id === itemId ? { ...it, estado } : it));
    // Estados son menos frecuentes (clicks discretos) — guardamos sin debounce
    upsertComentario({
      entrega_id: cur.id,
      item_id:    itemId,
      estado,
    });
  };

  /**
   * Edición del comentario general (conclusiones del docente).
   * Se guarda en bim_entregas.comentario_general con debounce 600ms y se
   * propaga al iframe vía postMessage para que la matriz interna refleje
   * el cambio en su última fila "Comentarios Generales".
   */
  const generalSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editComentarioGeneral = (texto: string) => {
    const cur = entregaSelRef.current;
    if (!cur) return;
    setComentarioGeneral(texto);
    // Update local entregaSel para que persista visualmente al re-render
    setEntregaSel(prev => prev ? { ...prev, comentario_general: texto } : prev);
    // Empujamos al iframe (la matriz interna lo refleja al instante)
    iframeRef.current?.contentWindow?.postMessage({
      type:    'updateComentarioGeneral',
      payload: { comentario_general: texto },
    }, '*');
    // Debounce save
    if (generalSaveTimer.current) clearTimeout(generalSaveTimer.current);
    generalSaveTimer.current = setTimeout(() => {
      updateEntregaComentarioGeneral(cur.id, texto);
    }, 600);
  };

  // ─── 6. Demo fallback (cuando no hay entrega real) ───────────────
  const loadDemoFallback = async () => {
    setLoading(true);
    try {
      const r = await fetch('/bim-dashboard/data.json?v=' + Date.now(), { cache: 'no-cache' });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      setData(j);
      setFileName('demo (data.json incluido)');
    } catch (e: any) {
      setError('No se pudo cargar el demo: ' + e.message);
      setLoading(false);
    }
  };

  // ─── 7. Subir / reemplazar data.json (docente) ───────────────────
  const onFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !isStaff) return;
    if (!alumnoSel) {
      setError('Elegí primero un alumno antes de cargar el JSON.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const text = String(ev.target?.result ?? '');
        // Validar que sea JSON parseable antes de subir
        JSON.parse(text);
        const blob = new Blob([text], { type: 'application/json' });

        setLoading(true);
        setError(null);

        let nueva: BimEntrega | null;
        if (entregaSel && entregaSel.alumno_id === alumnoSel
            && entregaSel.entrega_num === entregaNumDraft) {
          // Ya existe → reemplazar archivo de esa entrega (los comentarios y la
          // nota se preservan; solo cambia el data.json y el archivo_nombre).
          nueva = await replaceEntregaJSON(entregaSel, blob, file.name);
        } else {
          // No existe → crear, asociando el curso seleccionado para futuros
          // filtros y reportes.
          nueva = await createEntregaWithUpload({
            alumno_id:      alumnoSel,
            curso_id:       cursoSel || null,
            entrega_num:    entregaNumDraft,
            titulo:         `Entrega ${entregaNumDraft}`,
            archivo_nombre: file.name,
            jsonBlob:       blob,
          });
        }

        if (!nueva) {
          setError('No se pudo guardar la entrega. Revisá la consola.');
          setLoading(false);
          return;
        }

        // Refrescar lista del alumno y seleccionar la entrega nueva
        const list = await listEntregasOfAlumno(alumnoSel);
        setEntregas(list);
        setEntregaSel(list.find(x => x.id === nueva!.id) ?? nueva);
      } catch (err: any) {
        setError('JSON inválido: ' + err.message);
        setLoading(false);
      }
    };
    reader.readAsText(file);
  };

  // ─── 8. Acciones del footer docente ──────────────────────────────

  /**
   * Botón "Re-publicar con nueva nota" / "Publicar al alumno con nota".
   * Hace 3 cosas en una:
   *   1. Guarda la nota en bim_entregas.nota_final
   *   2. Setea status='corregido' (publica)
   *   3. Avisa al iframe vía updateEntregaMeta para que el banner de nota
   *      refleje el nuevo valor SIN tener que recargar la página (esto era
   *      el dolor original — antes había que F5 para ver la nota nueva).
   */
  const onPublicarConNota = async () => {
    if (!entregaSel) return;
    const v = notaDraft.trim();
    let nota: number | null = null;
    if (v !== '') {
      const n = parseFloat(v.replace(',', '.'));
      if (Number.isNaN(n) || n < 1 || n > 7) {
        setError('La nota debe ser un número entre 1.0 y 7.0');
        return;
      }
      nota = Math.round(n * 10) / 10;
    }

    setPublishing(true);
    setError(null);

    const okNota = await updateEntregaNota(entregaSel.id, nota);
    if (!okNota) { setError('No se pudo guardar la nota.'); setPublishing(false); return; }
    const okStatus = await updateEntregaStatus(entregaSel.id, 'corregido');
    if (!okStatus) { setError('No se pudo publicar.'); setPublishing(false); return; }

    // Sync a course_evaluations: la nota de la entrega N va al campo
    // evaluation_N (N=1,2,3) o final_evaluation (N=4). El docente puede
    // sobreescribirla manualmente desde "Mis Cursos → Evaluaciones del
    // Curso" — última escritura gana.
    if (entregaSel.curso_id) {
      const ok = await syncEntregaToEvaluation(
        entregaSel.curso_id,
        entregaSel.alumno_id,
        entregaSel.entrega_num,
        nota,
      );
      if (!ok) {
        // No abortamos la publicación si esto falla — el alumno igual ve la
        // entrega publicada. Solo logueamos. El docente puede ingresar la
        // nota manualmente en el modal si hace falta.
        console.warn('[CorreccionesBim] sync a course_evaluations falló pero la publicación se completó.');
      }
    } else {
      console.warn('[CorreccionesBim] entrega sin curso_id, no se sincroniza a course_evaluations.');
    }

    setEntregaSel({ ...entregaSel, nota_final: nota, status: 'corregido' });
    // Push al iframe → actualiza banner de nota sin recargar
    iframeRef.current?.contentWindow?.postMessage({
      type: 'updateEntregaMeta',
      payload: { nota_final: nota, status: 'corregido' },
    }, '*');
    setPublishing(false);
  };

  const onDespublicar = async () => {
    if (!entregaSel) return;
    setUnpublishing(true);
    const ok = await updateEntregaStatus(entregaSel.id, 'en_revision');
    if (ok) {
      setEntregaSel({ ...entregaSel, status: 'en_revision' });
      iframeRef.current?.contentWindow?.postMessage({
        type: 'updateEntregaMeta',
        payload: { status: 'en_revision' },
      }, '*');
    } else {
      setError('No se pudo despublicar.');
    }
    setUnpublishing(false);
  };

  /**
   * Borra la entrega seleccionada — archivo del bucket + fila en
   * bim_entregas (cascade limpia bim_comentarios). Pide confirmación
   * porque es destructivo e irreversible.
   */
  const onDeleteEntrega = async () => {
    if (!entregaSel) return;
    const al = alumnos.find(a => a.id === entregaSel.alumno_id);
    const alumnoLabel = al?.full_name || al?.email || 'el alumno';
    const ok = await confirm({
      title: `¿Borrar la Entrega ${entregaSel.entrega_num} de ${alumnoLabel}?`,
      description: (
        <div className="space-y-2">
          <p>Esta acción es <b>irreversible</b>. Vas a perder:</p>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>El archivo <code>data.json</code> de esta entrega.</li>
            <li>Todos los comentarios y estados que dejaste en la matriz.</li>
            <li>La nota final si la habías guardado.</li>
          </ul>
          <p className="text-amber-300 text-xs mt-2">
            El alumno dejará de ver esta entrega en su dashboard.
          </p>
        </div>
      ),
      confirmText: 'Sí, borrar',
      cancelText: 'Cancelar',
      tone: 'danger',
    });
    if (!ok) return;

    setDeleting(true);
    setError(null);
    const success = await deleteEntregaSb(entregaSel);
    if (!success) {
      setError('No se pudo borrar la entrega. Revisá la consola.');
      setDeleting(false);
      return;
    }
    // Limpiar estado local + refrescar lista del alumno
    const deletedId = entregaSel.id;
    setEntregaSel(null);
    setComentariosOverride({});
    setEstadosOverride({});
    setMatrizItems([]);
    if (entregaSel.alumno_id) {
      const list = await listEntregasOfAlumno(entregaSel.alumno_id);
      setEntregas(list.filter(e => e.id !== deletedId));
    }
    loadDemoFallback();
    setDeleting(false);
  };

  // ─── 9. Render ───────────────────────────────────────────────────
  const alumnoActual = useMemo(
    () => alumnos.find(a => a.id === (entregaSel?.alumno_id ?? alumnoSel)),
    [alumnos, alumnoSel, entregaSel?.alumno_id],
  );

  const statusChip = (s?: BimEntregaStatus | null) => {
    if (!s) return null;
    const map: Record<BimEntregaStatus, { txt: string; cls: string; Icon: typeof Clock }> = {
      pendiente:    { txt: 'Pendiente',    cls: 'chip-rose',  Icon: FileWarning  },
      en_revision:  { txt: 'En revisión',  cls: 'chip-amber', Icon: Clock        },
      corregido:    { txt: 'Publicada',    cls: 'chip-lime',  Icon: CheckCircle2 },
    };
    const { txt, cls, Icon } = map[s];
    return (
      <span className={`chip ${cls} inline-flex items-center gap-1`}>
        <Icon size={12} />{txt}
      </span>
    );
  };

  return (
    <div className="relative-z h-full flex flex-col">
      {/* ── Header ─────────────────────────────────────── */}
      <header className="px-6 pt-5 pb-3 flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[3px] uppercase font-semibold text-cubus-lime mb-1">
            Correcciones BIM
          </div>
          <h2 className="font-display text-2xl font-bold text-white tracking-tight">
            Dashboard del modelo
          </h2>
          <p className="text-sm text-white/60 mt-1">
            {isStaff
              ? 'Revisá entregas BIM por alumno, cargá nuevos data.json y dejá comentarios en la matriz.'
              : 'Tu modelo BIM analizado por entrega. Mirá los comentarios del docente en la pestaña Matriz de Revisión.'}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="chip chip-lime">
            {isStaff ? '◈ Docente · BIM Manager' : '◎ Alumno'}
          </span>
          {statusChip(entregaSel?.status)}
        </div>
      </header>

      {/* ── Toolbar de selección — oculta para alumno sin entregas ── */}
      {!(!isStaff && !loading && entregas.length === 0) && (
      <div className="px-6 pb-3">
        <div className="glass-surface rounded-xl border border-white/10 p-3 flex flex-wrap items-center gap-3">
          {isStaff ? (
            <>
              <label className="text-xs text-white/60 inline-flex items-center gap-2">
                <BookOpen size={12} /> Curso
              </label>
              <select
                value={cursoSel}
                onChange={(e) => {
                  const newId = e.target.value;
                  setCursoSel(newId);
                  // Persistimos. Cuando el docente cambia de curso a propósito
                  // limpiamos el alumno guardado: el alumno persistido pertenece
                  // a otro curso y el useEffect 2a igual lo descartaría.
                  writePersisted({ cursoId: newId, alumnoId: undefined });
                }}
                className="bg-cubus-deep border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white min-w-[220px]"
              >
                <option value="">— elegí un curso —</option>
                {cursos.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              <label className="text-xs text-white/60 inline-flex items-center gap-2 ml-2">
                <UserIcon size={12} /> Alumno
              </label>
              <select
                value={alumnoSel}
                onChange={(e) => {
                  setAlumnoSel(e.target.value);
                  writePersisted({ alumnoId: e.target.value });
                }}
                disabled={!cursoSel || alumnos.length === 0}
                className="bg-cubus-deep border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white min-w-[220px] disabled:opacity-50"
              >
                <option value="">
                  {!cursoSel
                    ? '— primero elegí un curso —'
                    : alumnos.length === 0
                      ? '— sin alumnos en este curso —'
                      : '— elegí un alumno —'}
                </option>
                {alumnos.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.full_name || a.email}
                  </option>
                ))}
              </select>

              <label className="text-xs text-white/60 ml-2">Entrega</label>
              <select
                value={entregaNumDraft}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  setEntregaNumDraft(n);
                  writePersisted({ entregaNum: n });
                  const match = entregas.find(x => x.entrega_num === n);
                  setEntregaSel(match ?? null);
                  if (!match) loadDemoFallback();
                }}
                className="bg-cubus-deep border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white"
              >
                {[1, 2, 3, 4].map(n => (
                  <option key={n} value={n}>
                    Entrega {n}{entregas.some(x => x.entrega_num === n) ? ' ●' : ''}
                  </option>
                ))}
              </select>

              <label className="btn-glass cursor-pointer ml-auto" title="Subir data.json del plugin">
                <Upload size={14} />
                <span>{entregaSel ? 'Reemplazar JSON' : 'Cargar JSON'}</span>
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={onFileUpload}
                  className="hidden"
                />
              </label>
              {entregaSel && (
                <button
                  onClick={onDeleteEntrega}
                  disabled={deleting}
                  className="btn-danger"
                  title="Borrar esta entrega (archivo + comentarios + nota)"
                >
                  <Trash2 size={14} />
                  <span>{deleting ? 'Borrando…' : 'Borrar entrega'}</span>
                </button>
              )}
              <button
                onClick={loadDemoFallback}
                className="btn-glass"
                title="Volver al data.json de demo"
              >
                <RotateCcw size={14} />
                <span>Demo</span>
              </button>
            </>
          ) : (
            <>
              <label className="text-xs text-white/60">Mis entregas</label>
              <select
                value={entregaSel?.id ?? ''}
                onChange={(e) => {
                  const ent = entregas.find(x => x.id === e.target.value) ?? null;
                  setEntregaSel(ent);
                  if (!ent) loadDemoFallback();
                }}
                className="bg-cubus-deep border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white min-w-[260px]"
              >
                {entregas.length === 0 && <option value="">— sin entregas aún —</option>}
                {entregas.map(e => (
                  <option key={e.id} value={e.id}>
                    Entrega {e.entrega_num} · {e.titulo || '—'} {e.status === 'corregido' ? '· ✓ publicada' : ''}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>
      )}

      {/* ── Indicador del archivo activo (oculto para alumno sin entregas) ── */}
      {!(!isStaff && !loading && entregas.length === 0) && (
      <div className="px-6 pb-2">
        <div className="inline-flex items-center gap-2 text-xs text-white/50">
          <FileJson size={12} />
          <span>{alumnoActual ? `${alumnoActual.full_name || alumnoActual.email} · ` : ''}Modelo:</span>
          <span className="text-white/80 font-medium">{fileName}</span>
        </div>
      </div>
      )}

      {error && (
        <div className="mx-6 mb-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm">
          ⚠ {error}
        </div>
      )}

      {/* ── Empty state ALUMNO sin entregas publicadas ──────────── */}
      {!isStaff && !loading && entregas.length === 0 ? (
        <div className="flex-1 px-6 pb-6 min-h-0 grid place-items-center">
          <div className="glass-surface rounded-2xl border border-white/10 p-10 text-center max-w-lg">
            <div className="w-16 h-16 rounded-full bg-cubus-lime/10 border border-cubus-lime/30 flex items-center justify-center mx-auto mb-4">
              <FileJson size={28} className="text-cubus-lime" />
            </div>
            <h3 className="text-display text-xl font-bold text-white mb-2">
              Aún no hay entrega publicada
            </h3>
            <p className="text-sm text-white/60 leading-relaxed">
              Tu docente todavía no ha publicado una corrección BIM para vos.
              Cuando suba y publique tu modelo, vas a verlo acá automáticamente
              con todas sus observaciones y nota.
            </p>
            <p className="text-xs text-white/40 mt-4">
              Tip: si recién enviaste el archivo a tu docente, puede demorar
              un día hábil en estar revisado.
            </p>
          </div>
        </div>
      ) : (

      /* ── Layout 2 columnas: iframe + sidebar matriz ─────────── */
      <div className="flex-1 px-6 pb-3 min-h-0 flex gap-3">
        {/* Iframe del dashboard */}
        <div className="flex-1 relative min-h-0">
          {loading && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none z-10 backdrop-blur-sm bg-cubus-bg/50 rounded-2xl">
              <Spinner size="lg" label="Cargando dashboard BIM" />
            </div>
          )}
          <iframe
            ref={iframeRef}
            src="/bim-dashboard/index.html"
            title="Correcciones BIM · Dashboard"
            className="w-full h-full rounded-2xl border border-white/10 bg-cubus-deep glass-surface"
          />
        </div>

        {/* Sidebar Matriz de Revisión — solo docente/admin */}
        {isStaff && (
          <MatrizSidebar
            items={matrizItems}
            comentarios={comentariosOverride}
            estados={estadosOverride}
            collapsed={matrizCollapsed}
            onToggle={() => setMatrizCollapsed(c => !c)}
            onEditComentario={editComentarioFromSidebar}
            onEditEstado={editEstadoFromSidebar}
            comentarioGeneral={comentarioGeneral}
            onEditComentarioGeneral={editComentarioGeneral}
            disabled={!entregaSel}
          />
        )}
      </div>
      )}

      {/* ── Footer docente: nota + re-publicar ───────────────── */}
      {isStaff && entregaSel && (
        <div className="px-6 pb-6 pt-1">
          <div className="glass-surface rounded-xl border border-white/10 p-3 flex flex-wrap items-center gap-3">
            <span className="text-[10px] tracking-[2px] uppercase text-white/40 mr-1">
              {entregaSel.status === 'corregido'
                ? 'Publicada · podés seguir editando nota y comentarios'
                : 'Borrador · editá libremente, después publicás'}
            </span>
            <label className="text-xs text-white/60">Nota final (1.0–7.0)</label>
            <input
              type="text"
              inputMode="decimal"
              value={notaDraft}
              onChange={(e) => setNotaDraft(e.target.value)}
              placeholder="—"
              className="bg-cubus-deep border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white w-24"
            />

            <div className="ml-auto flex items-center gap-2">
              {entregaSel.status === 'corregido' && (
                <button
                  onClick={onDespublicar}
                  disabled={unpublishing}
                  className="btn-glass"
                  title="Despublicar (volver a en revisión)"
                >
                  <EyeOff size={14} />
                  <span>{unpublishing ? 'Despublicando…' : 'Despublicar'}</span>
                </button>
              )}
              <button
                onClick={onPublicarConNota}
                disabled={publishing}
                className="btn-lime"
                title={entregaSel.status === 'corregido'
                  ? 'Re-publicar con nueva nota — actualiza el banner del alumno sin recargar'
                  : 'Publicar al alumno con la nota actual'}
              >
                <Send size={14} />
                <span>
                  {publishing
                    ? 'Publicando…'
                    : entregaSel.status === 'corregido'
                      ? 'Re-publicar con nueva nota'
                      : 'Publicar al alumno con nota'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   SIDEBAR · MATRIZ DE REVISIÓN (solo docente)
   ════════════════════════════════════════════════════════════════
   Refleja la matriz que el iframe ya parsea desde matriz.xlsx y
   emite vía postMessage `matrizReady`. Acá la mostramos al lado
   del dashboard para poder revisar dato + matriz a la vez (en vez
   de tener que cambiar de tab).
   Cualquier edición se persiste en bim_comentarios (Supabase) y
   se propaga al iframe vía updateComentariosOverride/updateEstadosOverride
   para que el tab interno del iframe muestre el cambio sin recargar.
   ──────────────────────────────────────────────────────────────── */

interface MatrizSidebarProps {
  items:                 MatrizItem[];
  comentarios:           ComentariosOverride;
  estados:               EstadosOverride;
  collapsed:             boolean;
  onToggle:              () => void;
  onEditComentario:      (itemId: string, value: string) => void;
  onEditEstado:          (itemId: string, estado: BimItemEstado) => void;
  /** Texto del comentario general / conclusiones del docente. */
  comentarioGeneral:     string;
  onEditComentarioGeneral: (value: string) => void;
  disabled:              boolean;
}

function MatrizSidebar({
  items, comentarios, estados, collapsed, onToggle,
  onEditComentario, onEditEstado,
  comentarioGeneral, onEditComentarioGeneral,
  disabled,
}: MatrizSidebarProps) {
  // Resumen
  const counts = useMemo(() => {
    const c = { cumple: 0, no_cumple: 0, pendiente: 0 } as Record<BimItemEstado, number>;
    items.forEach(it => {
      const ovr = estados[it.id];
      const e   = ovr || it.estado || 'pendiente';
      c[e] = (c[e] || 0) + 1;
    });
    return c;
  }, [items, estados]);

  // Agrupar por categoría preservando orden de aparición
  const grouped = useMemo(() => {
    const out: { categoria: string; items: MatrizItem[] }[] = [];
    const map = new Map<string, MatrizItem[]>();
    for (const it of items) {
      if (!map.has(it.categoria)) {
        const arr: MatrizItem[] = [];
        map.set(it.categoria, arr);
        out.push({ categoria: it.categoria, items: arr });
      }
      map.get(it.categoria)!.push(it);
    }
    return out;
  }, [items]);

  if (collapsed) {
    return (
      <button
        onClick={onToggle}
        className="self-start mt-2 btn-glass !p-2 h-10"
        title="Mostrar matriz de revisión"
      >
        <ListChecks size={14} />
      </button>
    );
  }

  return (
    <aside className="w-[360px] shrink-0 rounded-2xl border border-white/10 bg-cubus-deep/70 glass-surface flex flex-col min-h-0">
      {/* Header */}
      <div className="p-3 border-b border-white/10 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ListChecks size={14} className="text-cubus-lime shrink-0" />
          <h3 className="text-xs tracking-[2px] uppercase text-white/80 font-semibold truncate">
            Matriz de Revisión
          </h3>
        </div>
        <button
          onClick={onToggle}
          className="text-white/50 hover:text-white p-1 rounded"
          title="Ocultar matriz"
        >
          <ChevronUp size={14} />
        </button>
      </div>

      {/* Pills resumen */}
      <div className="p-3 pb-2 flex items-center gap-1.5 flex-wrap text-[11px]">
        <span className="chip chip-emerald"><b>{counts.cumple}</b> Cumple</span>
        <span className="chip chip-rose"><b>{counts.no_cumple}</b> No cumple</span>
        <span className="chip chip-amber"><b>{counts.pendiente}</b> Pendiente</span>
      </div>
      <p className="px-3 pb-2 text-[11px] text-white/50">
        Comentá y marcá cumple / no cumple. Sincroniza con el tab "Matriz de Revisión" del dashboard.
      </p>

      {/* Items */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-3">
        {disabled && (
          <div className="text-xs text-white/50 italic mt-4">
            Elegí un alumno y entrega para empezar a revisar.
          </div>
        )}
        {!disabled && items.length === 0 && (
          <div className="text-xs text-white/50 italic mt-4">
            Cargando matriz…
          </div>
        )}
        {grouped.map(g => (
          <div key={g.categoria}>
            <div className="text-[10px] tracking-[2px] uppercase text-cubus-lime/80 font-semibold mb-1.5">
              {g.categoria}
            </div>
            <div className="space-y-2">
              {g.items.map(it => {
                const estadoActual = estados[it.id] || it.estado || 'pendiente';
                // Si hay override del docente → ese; si no → el comentario
                // que el dashboard auto-generó (el mismo que se ve en la
                // tabla "Matriz de Revisión" del iframe).
                const override = comentarios[it.id];
                const isOverride = override !== undefined && override !== null;
                const valueToShow = isOverride ? (override as string) : (it.comentarioAuto || '');
                return (
                  <MatrizItemRow
                    key={it.id}
                    item={it}
                    estado={estadoActual}
                    valueToShow={valueToShow}
                    isOverride={isOverride}
                    autoComentario={it.comentarioAuto || ''}
                    onEditComentario={(v) => onEditComentario(it.id, v)}
                    onEditEstado={(e) => onEditEstado(it.id, e)}
                  />
                );
              })}
            </div>
          </div>
        ))}

        {/* ─── Comentarios Generales (siempre al final) ─────────────── */}
        {!disabled && (
          <ComentarioGeneralRow
            value={comentarioGeneral}
            onChange={onEditComentarioGeneral}
          />
        )}
      </div>
    </aside>
  );
}

interface MatrizItemRowProps {
  item:           MatrizItem;
  estado:         BimItemEstado;
  /** Texto a mostrar en la textarea: override si existe, sino auto-comentario. */
  valueToShow:    string;
  /** True si el valor actual es un override del docente (no el auto). */
  isOverride:     boolean;
  /** Comentario auto-generado por el dashboard, para botón "Restaurar". */
  autoComentario: string;
  onEditComentario: (v: string) => void;
  onEditEstado:     (e: BimItemEstado) => void;
}

function MatrizItemRow({
  item, estado, valueToShow, isOverride, autoComentario,
  onEditComentario, onEditEstado,
}: MatrizItemRowProps) {
  // Draft local — la fuente de verdad mientras el usuario tipea. Se
  // sincroniza con `valueToShow` cuando el valor cambia desde afuera (otra
  // fuente lo editó), pero NO si el usuario está editando este textarea
  // (se evita pisar lo que está escribiendo).
  const [draft, setDraft] = useState(valueToShow);
  const isFocusedRef = useRef(false);
  useEffect(() => {
    if (!isFocusedRef.current) setDraft(valueToShow);
  }, [valueToShow]);

  const estadoMap: Record<BimItemEstado, { label: string; cls: string }> = {
    cumple:    { label: '✓ Cumple',    cls: 'chip-emerald' },
    no_cumple: { label: '✗ No cumple', cls: 'chip-rose'    },
    pendiente: { label: '⏳ Pendiente', cls: 'chip-amber'   },
  };

  const cycleEstado = () => {
    const next: BimItemEstado =
      estado === 'pendiente' ? 'cumple'
      : estado === 'cumple'  ? 'no_cumple'
      :                        'pendiente';
    onEditEstado(next);
  };

  // Cada keystroke empuja el cambio (la lógica de debounce vive en el
  // padre — esto solo refleja en UI + propaga el último valor).
  const onChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setDraft(v);
    onEditComentario(v);
  };

  const onRestore = () => {
    setDraft(autoComentario);
    onEditComentario('');  // empty → quita el override → vuelve al auto
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <span className="text-[10px] font-mono text-white/40 mr-1.5">{item.id}</span>
          <span className="text-xs text-white/90 font-medium">{item.item}</span>
        </div>
        <button
          onClick={cycleEstado}
          className={`chip ${estadoMap[estado].cls} shrink-0 text-[10px] cursor-pointer hover:brightness-110`}
          title="Click para cambiar estado: pendiente → cumple → no cumple"
        >
          {estadoMap[estado].label}
        </button>
      </div>
      <textarea
        value={draft}
        onChange={onChangeText}
        onFocus={() => { isFocusedRef.current = true; }}
        onBlur={() => { isFocusedRef.current = false; }}
        placeholder="Comentario para el alumno…"
        rows={2}
        className="w-full bg-cubus-deep border border-white/10 rounded-md px-2 py-1.5 text-[11px] text-white/90 placeholder:text-white/30 resize-y focus:outline-none focus:border-cubus-lime/50"
      />
      {isOverride && autoComentario && (
        <button
          onClick={onRestore}
          className="text-[10px] text-cubus-lime/70 hover:text-cubus-lime mt-1"
          title="Volver al comentario que generó el dashboard automáticamente"
        >
          ↺ Restaurar comentario automático
        </button>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   COMENTARIO GENERAL · conclusiones del docente sobre la entrega
   ════════════════════════════════════════════════════════════════
   Va siempre al pie del sidebar (y de la matriz interna del dashboard).
   No tiene estado ni criterio — solo un textarea libre. Se persiste en
   bim_entregas.comentario_general (campo text en Supabase).
   El draft local mantiene foco mientras el padre debouncea el save. */

interface ComentarioGeneralRowProps {
  value:    string;
  onChange: (v: string) => void;
}

function ComentarioGeneralRow({ value, onChange }: ComentarioGeneralRowProps) {
  const [draft, setDraft] = useState(value);
  const isFocusedRef = useRef(false);
  useEffect(() => {
    if (!isFocusedRef.current) setDraft(value);
  }, [value]);

  return (
    <div className="mt-2 pt-3 border-t border-white/10">
      <div className="text-[10px] tracking-[2px] uppercase text-cubus-lime/80 font-semibold mb-1.5">
        Comentarios Generales
      </div>
      <p className="text-[10px] text-white/40 mb-1.5 italic">
        Conclusiones / observaciones globales sobre la entrega del alumno.
      </p>
      <textarea
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          onChange(e.target.value);
        }}
        onFocus={() => { isFocusedRef.current = true; }}
        onBlur={() => { isFocusedRef.current = false; }}
        placeholder="Escribí acá tus conclusiones para el alumno…"
        rows={4}
        className="w-full bg-cubus-deep border border-white/10 rounded-md px-2 py-1.5 text-[11px] text-white/90 placeholder:text-white/30 resize-y focus:outline-none focus:border-cubus-lime/50"
      />
    </div>
  );
}
